import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CardGiftcard as GiftIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { referralsApi } from '../services/api';
import { Referral, ReferralFilters } from '../types/api';
import { useDebounce } from '../hooks/useDebounce';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`referrals-tabpanel-${index}`}
      aria-labelledby={`referrals-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
  subtitle?: string;
}

function StatCard({ title, value, icon, color = '#1976d2', subtitle }: StatCardProps) {
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box
          sx={{
            p: 1,
            borderRadius: 2,
            backgroundColor: color + '15',
            color: color,
            mr: 2,
          }}
        >
          {icon}
        </Box>
        <Typography variant="h6" component="div">
          {title}
        </Typography>
      </Box>
      <Typography variant="h4" component="div" sx={{ mb: 1, fontWeight: 600 }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Paper>
  );
}

export default function Referrals() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [paymentFilter, setPaymentFilter] = useState<string>('all');
  const [rewardFilter, setRewardFilter] = useState<string>('all');
  const [tabValue, setTabValue] = useState(0);
  const [analyticsDays, setAnalyticsDays] = useState(30);

  const debouncedSearch = useDebounce(search, 500);

  const filters: ReferralFilters = {
    page: page + 1,
    size: rowsPerPage,
    search: debouncedSearch || undefined,
    made_payment: paymentFilter === 'all' ? undefined : paymentFilter === 'true',
    reward_given: rewardFilter === 'all' ? undefined : rewardFilter === 'true',
  };

  const { data: referralsData, isLoading, error } = useQuery({
    queryKey: ['referrals', filters],
    queryFn: () => referralsApi.getReferrals(filters),
  });

  const { data: analytics } = useQuery({
    queryKey: ['referralAnalytics', analyticsDays],
    queryFn: () => referralsApi.getReferralAnalytics(analyticsDays),
  });

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 4, fontWeight: 600 }}>
        Реферальная система
      </Typography>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            aria-label="referrals tabs"
            sx={{ px: 2 }}
          >
            <Tab icon={<PeopleIcon />} label="Рефералы" iconPosition="start" />
            <Tab icon={<TrendingUpIcon />} label="Аналитика" iconPosition="start" />
          </Tabs>
        </Box>

        <CardContent>
          <TabPanel value={tabValue} index={0}>
            {/* Фильтры */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Поиск"
                  placeholder="Поиск по username или Telegram ID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Оплата</InputLabel>
                  <Select
                    value={paymentFilter}
                    label="Оплата"
                    onChange={(e) => setPaymentFilter(e.target.value)}
                  >
                    <MenuItem value="all">Все</MenuItem>
                    <MenuItem value="true">Оплатили</MenuItem>
                    <MenuItem value="false">Не оплатили</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Награда</InputLabel>
                  <Select
                    value={rewardFilter}
                    label="Награда"
                    onChange={(e) => setRewardFilter(e.target.value)}
                  >
                    <MenuItem value="all">Все</MenuItem>
                    <MenuItem value="true">Выдана</MenuItem>
                    <MenuItem value="false">Не выдана</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Таблица */}
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error">
                Ошибка загрузки данных: {(error as Error).message}
              </Alert>
            ) : referralsData && referralsData.items.length > 0 ? (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Реферер</TableCell>
                        <TableCell>Реферал</TableCell>
                        <TableCell>Дата регистрации</TableCell>
                        <TableCell>Оплата</TableCell>
                        <TableCell>Дата оплаты</TableCell>
                        <TableCell>Награда</TableCell>
                        <TableCell>Тип подписки</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {referralsData.items.map((referral: Referral) => (
                        <TableRow key={referral.id} hover>
                          <TableCell>{referral.id}</TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {referral.referrer_username || 'Неизвестно'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {referral.referrer_telegram_id || referral.referrer_id}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {referral.referred_username || 'Неизвестно'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {referral.referred_telegram_id || referral.referred_user_id}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(referral.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={
                                referral.made_payment ? (
                                  <CheckCircleIcon />
                                ) : (
                                  <CancelIcon />
                                )
                              }
                              label={referral.made_payment ? 'Да' : 'Нет'}
                              color={referral.made_payment ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {referral.payment_date ? (
                              <Typography variant="body2">
                                {formatDate(referral.payment_date)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={
                                referral.reward_given ? (
                                  <GiftIcon />
                                ) : (
                                  <CancelIcon />
                                )
                              }
                              label={referral.reward_given ? 'Выдана' : 'Нет'}
                              color={referral.reward_given ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {referral.subscription_type ? (
                              <Chip label={referral.subscription_type} size="small" />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={referralsData.total}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  labelRowsPerPage="Строк на странице:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} из ${count}`
                  }
                />
              </>
            ) : (
              <Alert severity="info">Рефералы не найдены</Alert>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {analytics ? (
              <>
                {/* Статистика */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                      title="Всего рефералов"
                      value={analytics.total_referrals}
                      icon={<PeopleIcon />}
                      color="#1976d2"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                      title="Оплатили"
                      value={analytics.paid_referrals}
                      icon={<CheckCircleIcon />}
                      color="#2e7d32"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                      title="Конверсия"
                      value={`${analytics.conversion_rate}%`}
                      icon={<TrendingUpIcon />}
                      color="#ed6c02"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                      title="Награды выданы"
                      value={analytics.rewarded_referrals}
                      icon={<GiftIcon />}
                      color="#9c27b0"
                    />
                  </Grid>
                </Grid>

                {/* График рефералов по дням */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    Рефералы по дням
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analytics.referrals_by_date}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="total"
                        stroke="#1976d2"
                        name="Всего"
                      />
                      <Line
                        type="monotone"
                        dataKey="paid"
                        stroke="#2e7d32"
                        name="Оплатили"
                      />
                      <Line
                        type="monotone"
                        dataKey="rewarded"
                        stroke="#9c27b0"
                        name="Награды"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>

                <Grid container spacing={3}>
                  {/* Топ-10 рефереров */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Топ-10 рефереров
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Пользователь</TableCell>
                            <TableCell align="right">Рефералов</TableCell>
                            <TableCell align="right">Оплатили</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {analytics.top_referrers.map((referrer, index) => (
                            <TableRow key={referrer.referrer_id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>
                                  {index + 1}. {referrer.username || `User ${referrer.referrer_id}`}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                {referrer.referral_count}
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={referrer.paid_count}
                                  color="success"
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>

                  {/* Распределение по типам подписок */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Типы подписок
                    </Typography>
                    {analytics.subscription_distribution.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={analytics.subscription_distribution}
                            dataKey="count"
                            nameKey="type"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label
                          >
                            {analytics.subscription_distribution.map((_, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <Alert severity="info">Нет данных о типах подписок</Alert>
                    )}
                  </Grid>
                </Grid>
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
}
